import type { SourceMapInput } from '@jridgewell/trace-mapping';

import type { PageRuntimeSourceKind } from './compileAiComponent';

type EditorTransformSourceBase = {
  /** Stable identity of the source within a generation, chosen by the caller. */
  key: string;
  kind: PageRuntimeSourceKind;
  sourcePath: string;
  authoring: boolean;
};

/** One authoring source or an unchanged cached source in a generation. */
export type EditorTransformSource = EditorTransformSourceBase &
  (
    | { code: string; cacheToken?: never }
    | {
        /** Worker-local identity returned with the source's previous transform. */
        cacheToken: number;
        code?: never;
      }
  );

export type EditorTransformDiagnostic = {
  message: string;
  sourcePath: string;
  sourceLabel: string;
  line: number | null;
  column: number | null;
};

/**
 * `sourceLabel:line:column`, or null when the diagnostic has no source to point
 * at — a restarted compiler blames no line of the author's code.
 */
export function diagnosticLocation(diagnostic: EditorTransformDiagnostic): string | null {
  if (!diagnostic.sourceLabel) {
    return null;
  }

  return [diagnostic.sourceLabel, diagnostic.line, diagnostic.column]
    .filter((part) => part !== null)
    .join(':');
}

export type EditorTransformSourceResult =
  | {
      key: string;
      ok: true;
      referenced: false;
      code: string;
      sourceMap: SourceMapInput;
      sourceLabel: string;
      codeHash: string;
      cacheToken: number | null;
      fromCache: boolean;
      durationMs: number;
    }
  | {
      key: string;
      ok: true;
      referenced: true;
      cacheToken: number;
      fromCache: true;
      durationMs: number;
    }
  | {
      key: string;
      ok: false;
      cacheMiss: true;
    }
  | {
      key: string;
      ok: false;
      cacheMiss?: false;
      diagnostic: EditorTransformDiagnostic;
    };

/**
 * The preview stylesheet a generation compiled, or `null` on a surface that
 * takes its CSS from somewhere else — a published page's activated stylesheet
 * link, or the admin shell's browser runtime behind the component editor.
 */
export type EditorPreviewStylesheetResult =
  | {
      ok: true;
      css: string;
      candidateCount: number;
      fingerprint: string;
      durationMs: number;
    }
  | { ok: false; message: string };

export type EditorTransformCacheStats = {
  entries: number;
  bytes: number;
};

/**
 * `stylesheet` asks the worker to compile the generation's Tailwind CSS as one
 * step of the same round trip, so transformed code and the CSS it needs commit
 * together and no frame can show new code with old CSS.
 */
export type EditorTransformRequest =
  | {
      type: 'transform';
      generation: number;
      sources: EditorTransformSource[];
      stylesheet: boolean;
    }
  | { type: 'warm'; stylesheet: boolean };

export type EditorTransformResponse =
  | {
      type: 'result';
      generation: number;
      results: EditorTransformSourceResult[];
      stylesheet: EditorPreviewStylesheetResult | null;
      processTimeMs: number;
      cache: EditorTransformCacheStats;
    }
  | { type: 'warmed' };
